import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import {
  dashFetch,
  getActiveSite,
  setActiveSite as persistActiveSite
} from "@/api/client";
import {
  connectDashSocket,
  dashRpc as socketDashRpc,
  getDashSocket
} from "@/socket/dashSocket";
import { DASH_SOCKET_EVENTS } from "@/socket/events";
import { resolveDashboardHash } from "@/nav/navConfig";
import type {
  AuditEntry,
  DashboardStatus,
  DashboardTab,
  DashUser,
  LogEngagement,
  RequestLogEntry
} from "@/types/dashboard";
import {
  applyLogEngagementEvent,
  hydrateEngagementMaps,
  type LogEngagementEvent,
  patchRequestLogEngagement
} from "@/utils/requestLog";
import type { RequestLogFiltersState } from "@/features/request-log/useRequestLogPreferences";
import { requestLogFiltersAreDefault } from "@/features/request-log/requestLogFilters";
import { canonicalSiteHost, siteHostsMatch } from "@/utils/siteHost";

export type Tenant = {
  domain?: string;
  label?: string;
  name?: string;
  origin?: string;
  slug?: string;
  id?: string;
  status?: string;
  planId?: string;
  planName?: string;
  capabilities?: Record<string, boolean>;
  dashboardPreview?: boolean;
  onboardingStep?: number;
  activationPending?: boolean;
};

type RpcResponse = { ok?: boolean; status?: DashboardStatus; entries?: unknown[]; hasMore?: boolean; error?: string; message?: string };

function normSiteHost(s: string): string {
  return String(s || "").trim().toLowerCase();
}

function logEntryMatchesActiveSite(entry: RequestLogEntry, site: string): boolean {
  if (!site) return true;
  const siteL = normSiteHost(site);
  const entrySite = normSiteHost(String(entry.site || ""));
  if (entrySite) return entrySite === siteL;
  const entryHost = normSiteHost(String(entry.host || ""));
  return !entryHost || entryHost === siteL;
}

function filterLiveUsersForSite(list: unknown[], site: string, controlPlane: boolean): unknown[] {
  if (!Array.isArray(list)) return [];
  const siteC = canonicalSiteHost(site);
  if (!controlPlane || !siteC) return list;
  return list.filter((u) => {
    if (!u || typeof u !== "object") return false;
    const row = u as Record<string, unknown>;
    if (row.dashPreview) return false;
    const d = canonicalSiteHost(String(row.site || row.domain || row.httpHost || ""));
    return !d || siteHostsMatch(d, siteC);
  });
}

function normalizeStatusSocketsForSite(st: DashboardStatus, site: string, controlPlane: boolean): DashboardStatus {
  const sockets = (st.sockets && typeof st.sockets === "object" ? { ...(st.sockets as object) } : {}) as Record<
    string,
    unknown
  >;
  const usersTop = filterLiveUsersForSite(
    Array.isArray(sockets.usersTop) ? sockets.usersTop : [],
    site,
    controlPlane
  );
  sockets.usersTop = usersTop;
  sockets.registeredUsers = usersTop.length;
  sockets.connectedSockets = usersTop.length;
  return { ...st, sockets };
}

function tabFromHash(): DashboardTab {
  return resolveDashboardHash(location.hash || "");
}

export type DashboardContextValue = {
  activeSite: string;
  setActiveSite: (site: string) => Promise<void>;
  controlPlaneEnabled: boolean;
  status: DashboardStatus | null;
  applyStatus: (st: DashboardStatus) => void;
  user: DashUser;
  loadUser: () => Promise<void>;
  tenants: Tenant[];
  socketConnected: boolean;
  activeTab: DashboardTab;
  setActiveTab: (tab: DashboardTab) => void;
  logs: RequestLogEntry[];
  auditEntries: AuditEntry[];
  logsHasMore: boolean;
  auditHasMore: boolean;
  deployRunning: boolean;
  dashRpc: <TReq = unknown, TRes = unknown>(event: string, body?: TReq, timeoutMs?: number) => Promise<TRes>;
  loadStatus: (opts?: { force?: boolean }) => Promise<void>;
  loadLogs: (opts?: { reset?: boolean; filters?: RequestLogFiltersState | null }) => Promise<void>;
  setRequestLogAppendFilter: (fn: ((entry: RequestLogEntry) => boolean) | null) => void;
  loadAuditLog: (opts?: { reset?: boolean; category?: string }) => Promise<void>;
  loadTenants: () => Promise<void>;
  appendLog: (entry: RequestLogEntry) => void;
  appendAudit: (entry: AuditEntry, domain?: string) => void;
  logEngagementById: Record<string, LogEngagement>;
  logEngagementByUserUrl: Record<string, LogEngagement>;
};

const DashboardContext = createContext<DashboardContextValue | null>(null);

export function useDashboard(): DashboardContextValue {
  const ctx = useContext(DashboardContext);
  if (!ctx) throw new Error("useDashboard must be used within DashboardProvider");
  return ctx;
}

export function useOptionalDashboard(): DashboardContextValue | null {
  return useContext(DashboardContext);
}

export function DashboardProvider({ children }: { children: ReactNode }) {
  const controlPlaneEnabled = Boolean(
    typeof window !== "undefined" && (window as Window & { __CREA_CONTROL_PLANE?: boolean }).__CREA_CONTROL_PLANE
  );

  const [activeSite, setActiveSiteState] = useState(getActiveSite);
  const [status, setStatus] = useState<DashboardStatus | null>(null);
  const [user, setUser] = useState<DashUser>({});
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [socketConnected, setSocketConnected] = useState(false);
  const [activeTab, setActiveTabState] = useState<DashboardTab>(tabFromHash);
  const [logs, setLogs] = useState<RequestLogEntry[]>([]);
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const [logsHasMore, setLogsHasMore] = useState(false);
  const [auditHasMore, setAuditHasMore] = useState(false);
  const [deployRunning, setDeployRunning] = useState(false);
  const [logEngagementById, setLogEngagementById] = useState<Record<string, LogEngagement>>({});
  const [logEngagementByUserUrl, setLogEngagementByUserUrl] = useState<Record<string, LogEngagement>>({});

  const logsOffsetRef = useRef(0);
  const logsFiltersRef = useRef<RequestLogFiltersState | null>(null);
  const requestLogAppendFilterRef = useRef<((entry: RequestLogEntry) => boolean) | null>(null);
  const logEngagementByIdRef = useRef(logEngagementById);
  const logEngagementByUserUrlRef = useRef(logEngagementByUserUrl);
  const auditOffsetRef = useRef(0);
  const auditCategoryRef = useRef("");
  const statusRef = useRef<DashboardStatus | null>(null);
  const activeSiteRef = useRef(activeSite);
  const activeTabRef = useRef(activeTab);

  useEffect(() => { statusRef.current = status; }, [status]);
  useEffect(() => { activeSiteRef.current = activeSite; }, [activeSite]);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);
  useEffect(() => { logEngagementByIdRef.current = logEngagementById; }, [logEngagementById]);
  useEffect(() => { logEngagementByUserUrlRef.current = logEngagementByUserUrl; }, [logEngagementByUserUrl]);

  const mergeEngagementIntoLogs = useCallback((
    prev: RequestLogEntry[],
    byId: Record<string, LogEngagement>
  ): RequestLogEntry[] => {
    if (!prev.length || !Object.keys(byId).length) return prev;
    return prev.map((e) => {
      const id = e.id != null ? String(e.id) : "";
      const eng = id ? byId[id] : undefined;
      return eng ? patchRequestLogEngagement(e, eng) : e;
    });
  }, []);

  const applyEngagementEvent = useCallback((ev: LogEngagementEvent) => {
    if (!ev) return;
    if (!logEntryMatchesActiveSite({ host: ev.host, site: ev.site || ev.host }, activeSiteRef.current)) return;
    const { byId, byUserUrl } = applyLogEngagementEvent(
      ev,
      logEngagementByIdRef.current,
      logEngagementByUserUrlRef.current
    );
    logEngagementByIdRef.current = byId;
    logEngagementByUserUrlRef.current = byUserUrl;
    setLogEngagementById(byId);
    setLogEngagementByUserUrl(byUserUrl);
    const logId = ev.logId != null ? String(ev.logId) : "";
    if (!logId) return;
    const eng = byId[logId];
    if (!eng) return;
    setLogs((prev) => mergeEngagementIntoLogs(prev, { [logId]: eng }));
  }, [mergeEngagementIntoLogs]);

  const applyStatus = useCallback(
    (st: DashboardStatus) => {
      const site = activeSiteRef.current;
      setStatus(controlPlaneEnabled && site ? normalizeStatusSocketsForSite(st, site, true) : st);
    },
    [controlPlaneEnabled]
  );

  const dashRpc = useCallback(<TReq, TRes>(event: string, body?: TReq, timeoutMs?: number) => {
    return socketDashRpc<TReq, TRes>(event, body, timeoutMs);
  }, []);

  const loadUser = useCallback(async () => {
    try {
      const r = await dashFetch<{ email?: string; role?: string; isAdministrator?: boolean; user?: DashUser }>(
        "/dashboard/api/me",
        { cache: "no-store" }
      );
      setUser({
        email: r?.email || r?.user?.email,
        role: r?.role || r?.user?.role,
        isAdministrator: r?.isAdministrator ?? r?.user?.isAdministrator
      });
    } catch {
      setUser({});
    }
  }, []);

  const loadTenants = useCallback(async () => {
    try {
      const data = await dashFetch<{ tenants?: Tenant[] }>("/dashboard/api/tenants", { cache: "no-store" });
      setTenants(Array.isArray(data.tenants) ? data.tenants : []);
    } catch {
      setTenants([]);
    }
  }, []);

  const loadStatus = useCallback(async (opts?: { force?: boolean }) => {
    const force = Boolean(opts?.force);
    try {
      const s = getDashSocket();
      if (s?.connected && statusRef.current && !force) return;
      if (s?.connected) {
        const resp = await dashRpc<{ site?: string }, RpcResponse>(
          DASH_SOCKET_EVENTS.getSnapshot,
          { site: activeSiteRef.current }
        );
        if (resp?.ok && resp.status) {
          applyStatus(resp.status);
          return;
        }
      }
    } catch { /* REST fallback */ }
    try {
      const st = await dashFetch<DashboardStatus>("/dashboard/api/status", { cache: "no-store" });
      applyStatus(st);
    } catch {
      // Do not leave the UI stuck on an infinite loading state if status fetch fails.
      setStatus((prev) => prev || ({
        nowMs: Date.now(),
        server: { node: "—", uptimeSec: 0 },
        sockets: {},
        cache: { domains: [] },
        requests: {},
        origin: {},
        firewall: {},
        leads: {},
        domain: {}
      } as unknown as DashboardStatus));
    }
  }, [applyStatus, dashRpc]);

  const loadLogs = useCallback(async (opts?: { reset?: boolean; filters?: RequestLogFiltersState | null }) => {
    const reset = Boolean(opts?.reset);
    const filters =
      opts?.filters === null
        ? null
        : opts?.filters !== undefined
          ? opts.filters
          : logsFiltersRef.current;
    if (opts?.filters !== undefined) {
      logsFiltersRef.current = filters;
    }
    if (reset) {
      logsOffsetRef.current = 0;
      setLogsHasMore(false);
    }
    const limit = 100;
    const offset = logsOffsetRef.current;
    const useServerFilters = filters && !requestLogFiltersAreDefault(filters);
    const filterPayload = useServerFilters ? filters : undefined;
    try {
      const s = getDashSocket();
      if (s?.connected) {
        const resp = await dashRpc<{ limit: number; offset: number; filters?: RequestLogFiltersState }, RpcResponse>(
          "logs:init",
          { limit, offset, filters: filterPayload }
        );
        if (resp?.ok && Array.isArray(resp.entries)) {
          const chunk = resp.entries as RequestLogEntry[];
          setLogsHasMore(Boolean(resp.hasMore));
          setLogs((prev) => {
            const merged = reset ? chunk : prev.concat(chunk);
            const hydrated = hydrateEngagementMaps(merged);
            setLogEngagementById(hydrated.byId);
            setLogEngagementByUserUrl(hydrated.byUserUrl);
            logEngagementByIdRef.current = hydrated.byId;
            logEngagementByUserUrlRef.current = hydrated.byUserUrl;
            return merged.map((e) => {
              const id = e.id != null ? String(e.id) : "";
              const eng = id ? hydrated.byId[id] : undefined;
              return eng ? patchRequestLogEngagement(e, eng) : e;
            });
          });
          logsOffsetRef.current = offset + chunk.length;
          return;
        }
      }
    } catch { /* REST fallback */ }
    const filtersQ = filterPayload
      ? `&filters=${encodeURIComponent(JSON.stringify(filterPayload))}`
      : "";
    const data = await dashFetch<{ entries?: RequestLogEntry[]; hasMore?: boolean }>(
      `/dashboard/api/logs?limit=${limit}&offset=${offset}${filtersQ}`,
      { cache: "no-store" }
    );
    const chunk = Array.isArray(data.entries) ? data.entries : [];
    setLogsHasMore(Boolean(data.hasMore));
    setLogs((prev) => {
      const merged = reset ? chunk : prev.concat(chunk);
      const hydrated = hydrateEngagementMaps(merged);
      setLogEngagementById(hydrated.byId);
      setLogEngagementByUserUrl(hydrated.byUserUrl);
      logEngagementByIdRef.current = hydrated.byId;
      logEngagementByUserUrlRef.current = hydrated.byUserUrl;
      return merged.map((e) => {
        const id = e.id != null ? String(e.id) : "";
        const eng = id ? hydrated.byId[id] : undefined;
        return eng ? patchRequestLogEngagement(e, eng) : e;
      });
    });
    logsOffsetRef.current = offset + chunk.length;
  }, [dashRpc]);

  const setRequestLogAppendFilter = useCallback((fn: ((entry: RequestLogEntry) => boolean) | null) => {
    requestLogAppendFilterRef.current = fn;
  }, []);

  const loadAuditLog = useCallback(async (opts?: { reset?: boolean; category?: string }) => {
    const reset = Boolean(opts?.reset);
    const domain = activeSiteRef.current;
    if (!domain) {
      setAuditEntries([]);
      return;
    }
    if (reset) {
      auditOffsetRef.current = 0;
      setAuditHasMore(false);
    }
    const category = opts?.category !== undefined ? opts.category : auditCategoryRef.current;
    auditCategoryRef.current = category;
    const limit = 100;
    const offset = auditOffsetRef.current;
    try {
      const s = getDashSocket();
      if (s?.connected) {
        const resp = await dashRpc<{ domain: string; limit: number; offset: number; category?: string }, RpcResponse>(
          "audit:list",
          { domain, limit, offset, category: category || undefined }
        );
        if (resp?.ok && Array.isArray(resp.entries)) {
          const chunk = resp.entries as AuditEntry[];
          setAuditHasMore(Boolean(resp.hasMore));
          setAuditEntries((prev) => (reset ? chunk : prev.concat(chunk)));
          auditOffsetRef.current = offset + chunk.length;
          return;
        }
      }
    } catch { /* REST fallback */ }
    let url = `/dashboard/api/audit?domain=${encodeURIComponent(domain)}&limit=${limit}&offset=${offset}`;
    if (category) url += `&category=${encodeURIComponent(category)}`;
    const data = await dashFetch<{ entries?: AuditEntry[]; hasMore?: boolean }>(url, { cache: "no-store" });
    const chunk = Array.isArray(data.entries) ? data.entries : [];
    setAuditHasMore(Boolean(data.hasMore));
    setAuditEntries((prev) => (reset ? chunk : prev.concat(chunk)));
    auditOffsetRef.current = offset + chunk.length;
  }, [dashRpc]);

  const appendLog = useCallback((entry: RequestLogEntry) => {
    if (!entry) return;
    if (!logEntryMatchesActiveSite(entry, activeSiteRef.current)) return;
    const appendFilter = requestLogAppendFilterRef.current;
    if (appendFilter && !appendFilter(entry)) return;
    const id = entry.id != null ? String(entry.id) : "";
    const maxRows =
      logsFiltersRef.current && !requestLogFiltersAreDefault(logsFiltersRef.current) ? 100 : 2000;
    setLogs((prev) => [entry, ...prev].slice(0, maxRows));
  }, []);

  const appendAudit = useCallback((entry: AuditEntry, domain?: string) => {
    const d = activeSiteRef.current;
    if (d && domain && String(domain) !== String(d)) return;
    if (!entry?.id) return;
    setAuditEntries((prev) => {
      if (prev.some((x) => String(x.id) === String(entry.id))) return prev;
      return [entry, ...prev].slice(0, 2000);
    });
  }, []);

  const setActiveSite = useCallback(async (site: string) => {
    const next = String(site || "").trim();
    if (!next || next === activeSiteRef.current) return;
    persistActiveSite(next);
    setActiveSiteState(next);
    activeSiteRef.current = next;
    setStatus(null);
    setLogs([]);
    setAuditEntries([]);
    setLogEngagementById({});
    setLogEngagementByUserUrl({});
    logEngagementByIdRef.current = {};
    logEngagementByUserUrlRef.current = {};
    setLogsHasMore(false);
    setAuditHasMore(false);
    logsOffsetRef.current = 0;
    logsFiltersRef.current = null;
    auditOffsetRef.current = 0;
    try {
      const u = new URL(location.href);
      u.searchParams.set("site", next);
      history.replaceState({}, "", u.pathname + u.search + u.hash);
    } catch { /* ignore */ }
  }, []);

  const setActiveTab = useCallback((tab: DashboardTab) => {
    setActiveTabState(tab);
    activeTabRef.current = tab;
    const h = `#${tab}`;
    if (location.hash !== h) history.replaceState(null, "", h);
  }, []);

  useEffect(() => {
    const onHash = () => setActiveTabState(tabFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    void loadUser();
    void loadTenants();
  }, [loadUser, loadTenants]);

  /** Keep dashboard sign-in alive while the tab is open (server extends Redis + cookie TTL). */
  useEffect(() => {
    const touch = () => {
      void loadUser();
    };
    const intervalMs = 5 * 60 * 1000;
    const id = window.setInterval(touch, intervalMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") touch();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", touch);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", touch);
    };
  }, [loadUser]);

  useEffect(() => {
    const s = connectDashSocket(activeSite);

    const reloadSiteData = () => {
      void loadStatus({ force: true });
      void loadLogs({ reset: true });
      if (activeTabRef.current === "audit-log") void loadAuditLog({ reset: true });
    };

    const onConnect = () => {
      setSocketConnected(true);
      reloadSiteData();
    };
    const onDisconnect = () => setSocketConnected(false);
    const onSnapshot = (st: DashboardStatus) => { if (st) applyStatus(st); };
    const onStatus = (st: DashboardStatus) => { if (st) applyStatus(st); };

    const onAuditEvent = (data: { domain?: string; entry?: AuditEntry }) => {
      if (data?.entry) appendAudit(data.entry, data.domain);
    };

    const onLogsAppend = (entry: RequestLogEntry) => appendLog(entry);

    const onFirewallAppend = (ev: Record<string, unknown>) => {
      if (!ev) return;
      const site = activeSiteRef.current;
      if (site && ev.host && String(ev.host).toLowerCase() !== String(site).toLowerCase()) return;
      setStatus((prev) => {
        if (!prev) return prev;
        const fw = (prev.firewall && typeof prev.firewall === "object" ? { ...(prev.firewall as object) } : {}) as Record<string, unknown>;
        const last24h = (fw.last24h && typeof fw.last24h === "object" ? { ...(fw.last24h as object) } : {}) as Record<string, unknown>;
        const recent = Array.isArray(last24h.recent) ? [...last24h.recent] : [];
        recent.push(ev);
        const act = String(ev.action || "firewall_unknown");
        const byAction = (last24h.byAction && typeof last24h.byAction === "object" ? { ...(last24h.byAction as object) } : {}) as Record<string, number>;
        byAction[act] = (byAction[act] || 0) + 1;
        last24h.recent = recent;
        last24h.byAction = byAction;
        last24h.total = Number.isFinite(last24h.total as number) ? (Number(last24h.total) + 1) : 1;
        fw.last24h = last24h;
        return { ...prev, firewall: fw };
      });
    };

    const onUsersUpdate = (list: unknown[]) => {
      const filtered = filterLiveUsersForSite(list, activeSiteRef.current, controlPlaneEnabled);
      setStatus((prev) => {
        const base = prev || { server: { node: "—", uptimeSec: 0 }, sockets: {}, cache: { domains: [] }, requests: {}, origin: {}, firewall: {}, leads: {}, domain: {} };
        const sockets = (base.sockets && typeof base.sockets === "object" ? { ...(base.sockets as object) } : {}) as Record<string, unknown>;
        sockets.usersTop = filtered.slice(0, 30);
        sockets.registeredUsers = filtered.length;
        sockets.connectedSockets = filtered.length;
        return { ...base, sockets };
      });
    };

    const onDeployProgress = (ev: { status?: string; domain?: string }) => {
      const running = ev?.status === "running" || ev?.status === "queued";
      setDeployRunning(running);
    };

    s.on("connect", onConnect);
    s.on("disconnect", onDisconnect);
    s.on(DASH_SOCKET_EVENTS.snapshot, onSnapshot);
    s.on(DASH_SOCKET_EVENTS.status, onStatus);
    s.on(DASH_SOCKET_EVENTS.auditEvent, onAuditEvent);
    s.on(DASH_SOCKET_EVENTS.logsAppend, onLogsAppend);
    s.on(DASH_SOCKET_EVENTS.logsEngagement, applyEngagementEvent);
    s.on(DASH_SOCKET_EVENTS.firewallAppend, onFirewallAppend);
    s.on(DASH_SOCKET_EVENTS.usersUpdate, onUsersUpdate);
    s.on(DASH_SOCKET_EVENTS.redirectsAppend, () => { /* tabs reload on mount */ });
    s.on(DASH_SOCKET_EVENTS.redirectsCleared, () => {});
    s.on(DASH_SOCKET_EVENTS.redirectsDeleted, () => {});
    s.on(DASH_SOCKET_EVENTS.formsAppend, () => {});
    s.on(DASH_SOCKET_EVENTS.formsCleared, () => {});
    s.on(DASH_SOCKET_EVENTS.formsUpdated, () => {});
    s.on(DASH_SOCKET_EVENTS.originEvent, () => {});
    s.on(DASH_SOCKET_EVENTS.originHealth, () => { void loadStatus({ force: true }); });
    s.on(DASH_SOCKET_EVENTS.queueChanged, () => {});
    s.on(DASH_SOCKET_EVENTS.preloadStatus, () => {});
    s.on(DASH_SOCKET_EVENTS.preloadProgress, () => {});
    s.on(DASH_SOCKET_EVENTS.siteStructureUpdate, () => {});
    s.on(DASH_SOCKET_EVENTS.deployProgress, onDeployProgress);
    s.on(DASH_SOCKET_EVENTS.leadsGroupsChanged, () => {});
    s.on(DASH_SOCKET_EVENTS.leadsChanged, () => {});
    s.on(DASH_SOCKET_EVENTS.userEventsAppend, () => {});
    s.on(DASH_SOCKET_EVENTS.userState, () => {});
    s.on(DASH_SOCKET_EVENTS.userEvent, () => {});

    setSocketConnected(s.connected);
    reloadSiteData();

    return () => {
      s.off("connect", onConnect);
      s.off("disconnect", onDisconnect);
      s.off(DASH_SOCKET_EVENTS.snapshot, onSnapshot);
      s.off(DASH_SOCKET_EVENTS.status, onStatus);
      s.off(DASH_SOCKET_EVENTS.auditEvent, onAuditEvent);
      s.off(DASH_SOCKET_EVENTS.logsAppend, onLogsAppend);
      s.off(DASH_SOCKET_EVENTS.logsEngagement, applyEngagementEvent);
      s.off(DASH_SOCKET_EVENTS.firewallAppend, onFirewallAppend);
      s.off(DASH_SOCKET_EVENTS.usersUpdate, onUsersUpdate);
      s.off(DASH_SOCKET_EVENTS.deployProgress, onDeployProgress);
      s.off(DASH_SOCKET_EVENTS.originHealth);
    };
  }, [activeSite, applyEngagementEvent, applyStatus, appendAudit, appendLog, controlPlaneEnabled]);

  const value = useMemo<DashboardContextValue>(() => ({
    activeSite,
    setActiveSite,
    controlPlaneEnabled,
    status,
    applyStatus,
    user,
    loadUser,
    tenants,
    socketConnected,
    activeTab,
    setActiveTab,
    logs,
    auditEntries,
    logsHasMore,
    auditHasMore,
    deployRunning,
    dashRpc,
    loadStatus,
    loadLogs,
    setRequestLogAppendFilter,
    loadAuditLog,
    loadTenants,
    appendLog,
    appendAudit,
    logEngagementById,
    logEngagementByUserUrl
  }), [
    activeSite, setActiveSite, controlPlaneEnabled, status, applyStatus, user, loadUser,
    tenants, socketConnected, activeTab, setActiveTab, logs, auditEntries, logsHasMore,
    auditHasMore, deployRunning, dashRpc, loadStatus, loadLogs, setRequestLogAppendFilter,
    loadAuditLog, loadTenants, appendLog, appendAudit, logEngagementById, logEngagementByUserUrl
  ]);

  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
}
